import { defineMessages, useIntl } from 'react-intl';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { Box } from '@cbhq/cds-web/layout/Box';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { Tooltip } from '@cbhq/cds-web/overlays/tooltip/Tooltip';
import { Text } from '@cbhq/cds-web/typography/Text';

import { CountOrSingleCell } from '../../../components/CountOrSingleCell';

type TeamHeaderProps = {
  orgName: string;
  orgLogoSrc: string;
  memberCount: number;
  entities: string[];
  onInvite?: () => void;
  isInviteDisabled?: boolean;
};

const LOGO_BOX_STYLE = { overflow: 'hidden', flexShrink: 0 };

const messages = defineMessages({
  memberCount: {
    id: 'teamHeader.memberCount',
    defaultMessage: '{count, plural, one {# team member} other {# team members}}',
    description: 'Member count shown below the org name',
  },
  entities: {
    id: 'teamHeader.entities',
    defaultMessage: '{count, plural, one {# entity} other {# entities}}',
    description: 'Tooltip trigger showing how many entities the org has',
  },
  inviteTeamMember: {
    id: 'teamHeader.inviteTeamMember',
    defaultMessage: 'Invite team member',
    description: 'Button label to open the invite team member flow',
  },
  orgLogoAlt: {
    id: 'teamHeader.orgLogoAlt',
    defaultMessage: '{orgName} logo',
    description: 'Alt text for the organization logo image',
  },
  inviteDisabledTooltip: {
    id: 'teamHeader.inviteDisabledTooltip',
    defaultMessage:
      'You are not allowed to invite members. Talk to your organization administrator for access.',
    description: 'Tooltip shown on the disabled invite button when the user cannot manage members',
  },
});

export function TeamHeader({
  orgName,
  orgLogoSrc,
  memberCount,
  entities,
  onInvite,
  isInviteDisabled,
}: TeamHeaderProps) {
  const { formatMessage } = useIntl();

  const inviteButton = (
    <Button
      variant="primary"
      compact
      disabled={isInviteDisabled || onInvite == null}
      onClick={onInvite}
      data-dd-action-name="team_invite_open"
      data-testid="team-invite-button"
    >
      {formatMessage(messages.inviteTeamMember)}
    </Button>
  );

  return (
    <VStack gap={4} width="100%">
      {/* Title row */}
      <Box flexDirection="row" alignItems="center" justifyContent="space-between">
        <Box flexDirection="row" alignItems="center" gap={4}>
          {orgLogoSrc && (
            /* Raw <img> rather than CDS Avatar: Avatar's fixed size tokens don't map to
               --space-8, and its shape options don't include the rounded-rectangle the
               design calls for. Manual sizing and clipping give exact control. */
            <Box
              borderRadius={400}
              width="var(--space-8)"
              height="var(--space-8)"
              style={LOGO_BOX_STYLE}
            >
              <img
                src={orgLogoSrc}
                alt={formatMessage(messages.orgLogoAlt, { orgName })}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
          )}
          <VStack gap={0.25}>
            <Text as="h1" font="title1" data-testid="team-header-org-name">
              {orgName}
            </Text>
            <Box flexDirection="row" alignItems="center" gap={1}>
              <Text font="body" color="fgMuted" data-testid="team-header-member-count">
                {formatMessage(messages.memberCount, { count: memberCount })}
              </Text>
              {entities.length > 0 && (
                <>
                  <Text font="body" color="fgMuted">
                    ·
                  </Text>
                  <CountOrSingleCell
                    items={entities}
                    countMessage={messages.entities}
                    color="fgMuted"
                    font="body"
                    alwaysShowCount
                  />
                </>
              )}
            </Box>
          </VStack>
        </Box>

        {isInviteDisabled ? (
          <Tooltip content={formatMessage(messages.inviteDisabledTooltip)}>
            {/* Box wrapper ensures the tooltip receives hover events on the disabled button */}
            <Box>{inviteButton}</Box>
          </Tooltip>
        ) : (
          inviteButton
        )}
      </Box>
    </VStack>
  );
}
